import {useEffect, useRef} from 'react';

/**
 * Таймер, который умеет вставать на паузу: пока isPaused, время не идет, после
 * паузы досчитывается остаток. Смена resetKey начинает отсчет заново (новое
 * уведомление). Без enabled или без delay таймер не идет и не копит время.
 * Срабатывает один раз на resetKey.
 *
 * @param {function(): void} callback
 * @param {number|null} delay — мс
 * @param {{isPaused?: boolean, resetKey?: *, enabled?: boolean}} [options]
 */
export const usePausableTimeout = (callback, delay, {isPaused = false, resetKey, enabled = true} = {}) => {
  const callbackRef = useRef(callback);
  callbackRef.current = callback;
  const stateRef = useRef({key: undefined, elapsed: 0, fired: false});

  const isActive = enabled && typeof delay === 'number';
  const state = stateRef.current;
  if (state.key !== resetKey) {
    stateRef.current = {key: resetKey, elapsed: 0, fired: false};
  }

  useEffect(() => {
    const current = stateRef.current;
    if (!isActive || isPaused || current.fired) {
      return undefined;
    }
    const startedAt = Date.now();
    const timer = setTimeout(() => {
      current.fired = true;
      callbackRef.current();
    }, Math.max(0, delay - current.elapsed));
    return () => {
      clearTimeout(timer);
      if (!current.fired) {
        current.elapsed += Date.now() - startedAt;
      }
    };
  }, [isActive, isPaused, delay, resetKey]);
};
